import { Button, DatePicker, Input, Space } from 'antd';
import FormItem from 'antd/lib/form/FormItem';
import type React from 'react';
import { useMemo, useState } from 'react';
import type {
  Department,
  GetOrderByIdWithDriversWarehousesQuery,
} from '@/generated/graphql/graphql';
import dayjs from '@/utils/dayjs-instance';
import { WorkloadSelect } from '../../../components/WorkloadsSelect';
import { useGetDistrictScheduleAndWorkload } from '../../../query/orders.hooks';
import { parseDistrictSchedule } from '../../../utils/districtScheduleUtils';
import {
  isOrderPlannedDateCalendarDayDisabled,
  isoPlannedDateAfterCalendarPick,
} from './plannedDateCalendar';

interface OrderDetailsSectionProps {
  orderData: NonNullable<GetOrderByIdWithDriversWarehousesQuery['order_by_pk']>;
  selectedDepartment?: Department;
  onFieldChange: (field: string, value: string | number) => void;
  onInsertUserViewsLog: () => void;
  isOrderIdExists: boolean;
  isUserIdExists: boolean;
  currentPlannedDate?: string;
  currentPlannedDateDuration?: number;
  currentFullname?: string;
  currentPhone?: string;
  isDisabled?: boolean;
}

export const OrderDetailsSection: React.FC<OrderDetailsSectionProps> = ({
  orderData,
  onFieldChange,
  onInsertUserViewsLog,
  isOrderIdExists,
  isUserIdExists,
  currentPlannedDate,
  currentPlannedDateDuration,
  currentFullname,
  currentPhone,
  isDisabled = false,
}) => {
  const [isPhonePressed, setIsPhonePressed] = useState(false);
  const districtId = orderData.client?.districtId ?? '';
  const plannedDate = currentPlannedDate || orderData.plannedDate;
  const plannedDateStr = plannedDate
    ? dayjs(plannedDate).utc().format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD');
  const nextDayStr = dayjs(plannedDateStr).add(1, 'day').format('YYYY-MM-DD');
  const { data: districtData } = useGetDistrictScheduleAndWorkload({
    districtId,
    plannedDate: dayjs.utc(plannedDateStr, 'YYYY-MM-DD').startOf('day').toISOString(),
    nextDay: dayjs.utc(nextDayStr, 'YYYY-MM-DD').startOf('day').toISOString(),
  });
  const districtSchedule = useMemo(() => parseDistrictSchedule(districtData), [districtData]);
  const plannedDateDuration = currentPlannedDateDuration || orderData.plannedDateDuration;
  return (
    <>
      <FormItem className="app-form-item" label="Planned date">
        <Space.Compact style={{ width: '100%', display: 'flex' }}>
          <FormItem className="app-form-item">
            <DatePicker
              format={'YYYY-MM-DD'}
              disabled={isDisabled}
              disabledDate={(current) =>
                isOrderPlannedDateCalendarDayDisabled(current, {
                  districtSchedule,
                })
              }
              value={plannedDate ? dayjs.utc(plannedDate) : undefined}
              onChange={(e) => {
                if (e) {
                  onFieldChange('plannedDate', isoPlannedDateAfterCalendarPick(e, plannedDate));
                } else {
                  onFieldChange('plannedDate', '');
                }
              }}
              allowClear={false}
              superPrevIcon={null}
              superNextIcon={null}
            />
          </FormItem>
          <WorkloadSelect
            handleFieldChange={(path: string[], value: string | number) => {
              if (path.length > 0) {
                onFieldChange(path[path.length - 1], value);
              }
            }}
            orderData={
              {
                order_by_pk: {
                  ...orderData,
                  plannedDate,
                  plannedDateDuration,
                },
              } as GetOrderByIdWithDriversWarehousesQuery
            }
            isDisabled={isDisabled}
          />
        </Space.Compact>
      </FormItem>

      <FormItem className="app-form-item" label="Fullname">
        <Input
          placeholder="Fullname"
          value={currentFullname || orderData.client?.fullname}
          onChange={(e) => onFieldChange('fullname', e.target.value)}
          disabled={isDisabled}
        />
      </FormItem>

      <FormItem className="app-form-item" label="Phone">
        {isPhonePressed ? (
          <Input
            placeholder="Phone"
            value={currentPhone || orderData.client?.phone}
            onChange={(e) => onFieldChange('phone', e.target.value)}
            disabled={isDisabled}
          />
        ) : (
          <Button
            onClick={() => {
              setIsPhonePressed(true);
              if (isOrderIdExists && isUserIdExists) {
                onInsertUserViewsLog();
              }
            }}
            block
            type="primary"
            disabled={isDisabled}
          >
            Click to see phone
          </Button>
        )}
      </FormItem>
    </>
  );
};
